import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { getInstallationId, learnerApiJson } from '@/lib/learner-api';
import { useLearnerAuth } from '@/providers/LearnerAuth';
import { typeScale } from '@/lib/typography';

type Entry = { rank: number; nickname: string; score: number; is_you?: boolean };
type Board = { entries?: Entry[]; player_entry?: Entry | null; personal_best?: number | null } | Entry[];

export const Route = createFileRoute('/app/games/$slug/leaderboard')({ component: GameLeaderboard });

function GameLeaderboard() {
  const { slug } = Route.useParams();
  const gameSlug = slug === 'ya-ya' ? 'flappy_hmong' : slug === 'nab' ? 'snake' : slug;
  const auth = useLearnerAuth();
  const board = useQuery({
    queryKey: ['game-leaderboard', gameSlug],
    queryFn: () =>
      learnerApiJson<Board>(
        auth?.getToken ?? null,
        `/v1/games/${gameSlug}/leaderboard?installation_id=${getInstallationId()}`,
      ),
  });
  const payload = Array.isArray(board.data) ? { entries: board.data } : board.data;
  const entries = payload?.entries ?? [];
  const playerEntry = payload && 'player_entry' in payload ? payload.player_entry : null;
  const personalBest = payload && 'personal_best' in payload ? payload.personal_best : null;
  return (
    <div className="mx-auto max-w-2xl px-5 py-8">
      <Link to="/app/games/$slug" params={{ slug }} className="font-bold text-[#2d7a13]">
        ← Back to game
      </Link>
      <h1 className={`mt-7 ${typeScale.screenTitle}`}>Leaderboard</h1>
      {personalBest != null ? <p className="mt-2 text-slate-600">Your best: {personalBest}</p> : null}
      <div className="mt-7 overflow-hidden rounded-3xl border border-[#cfe8c6] bg-white">
        {entries.map((entry) => (
          <div
            key={`${entry.rank}-${entry.nickname}`}
            className={`flex gap-4 border-b p-4 last:border-0 ${entry.is_you ? 'bg-[#ECFFE6]' : ''}`}
          >
            <strong className="w-8">{entry.rank}</strong>
            <span className="flex-1 font-bold">{entry.nickname}</span>
            <span>{entry.score}</span>
          </div>
        ))}
        {playerEntry ? (
          <div className="flex gap-4 border-t border-dashed bg-[#ECFFE6] p-4">
            <strong className="w-8">{playerEntry.rank}</strong>
            <span className="flex-1 font-bold">{playerEntry.nickname}</span>
            <span>{playerEntry.score}</span>
          </div>
        ) : null}
        {!entries.length && <p className="p-6 text-slate-500">No scores yet.</p>}
      </div>
    </div>
  );
}
